import apiClient from './api';
import { ApiResponse } from '../types';

export interface UsageSummary {
  provider: string;
  model: string;
  operation_type: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  request_count: number;
}

export interface DailyUsage {
  date: string;
  provider: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  request_count: number;
}

export interface AgentUsage {
  agent_id: string;
  agent_name: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  request_count: number;
}

export interface TotalUsage {
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  request_count: number;
  by_provider: Record<string, {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    cost_usd: number;
    request_count: number;
  }>;
}

export interface OperationUsage {
  operation_type: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  request_count: number;
}

class UsageService {
  async getTotalUsage(): Promise<TotalUsage> {
    const response = await apiClient.get<ApiResponse<TotalUsage>>('/usage/total');
    if (response.data.data) return response.data.data;
    throw new Error('Failed to get total usage');
  }

  async getUsageSummary(from?: string, to?: string): Promise<UsageSummary[]> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse<UsageSummary[]>>(`/usage/summary${query}`);
    return response.data.data || [];
  }

  async getUsageDaily(days: number = 30): Promise<DailyUsage[]> {
    const response = await apiClient.get<ApiResponse<DailyUsage[]>>(`/usage/daily?days=${days}`);
    return response.data.data || [];
  }

  async getUsageByAgent(from?: string, to?: string): Promise<AgentUsage[]> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse<AgentUsage[]>>(`/usage/by-agent${query}`);
    return response.data.data || [];
  }

  async getUsageByOperation(from?: string, to?: string): Promise<OperationUsage[]> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse<OperationUsage[]>>(`/usage/by-operation${query}`);
    return response.data.data || [];
  }
}

export const usageService = new UsageService();
